import React, { useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { Col, Label, Nav, NavItem, NavLink, Row } from "reactstrap";
import classnames from "classnames";
import LoadingSpinner from "../Spinner";
import TableContainer from "../TableContainer";
import Select from "react-select";
import { getCustomersListData } from "../../../store/customers/action";
import {
  getSingleCustomerJobsList,
  searchSingleJobFilesList,
} from "../../../store/jobsList/actions";
import { searchSingleCustomerInvoiceList } from "../../../store/invoiceCrmIT/action";
import HandleDateTime from "../HandleDateTime";
import { taskCheckListSearch } from "../../../store/checkList/action";
import Checklist from "../../../store/checkList/reducer";
import { proformaInvoiceSearch } from "../../../store/ProformaInvoice/action";
import ProformaInvoice from "../../../store/ProformaInvoice/reducer";
import { getSingleCustomerQuoteList } from "../../../store/quote/action";
import { searchCustomerAttachmentApiCall } from "../../../helpers/fakebackend_helper";

const SearchAttachments = ({
  emailCustomerList,
  attachments,
  setAttachments,
  activeSearchTab,
  setActiveSearchTab,
  sortedCustomerId,
  setSortedCustomerId,
  setSelectedJobsId,
  selectedJobsId,
  // Change #100123 - the record's own customer (the invoice's, say), used for
  // Customer Attachments when nobody on the To line resolves to a customer.
  fallbackCustomerId,
  // Change #100688 - the record's own jobs (the Job_Ids on its item lines),
  // preselected in the Job List the same way.
  fallbackJobIds,
}) => {
  const dispatch = useDispatch();

  const { singleCustomerJobsListDataAPI } = useSelector((sate) => ({
    singleCustomerJobsListDataAPI:
      sate.Jobs.singleCustomerJobsListData?.job_list,
  }));

  const [selectedInvoiceId, setSelectedInvoiceId] = useState([]);
  const [selectedQuotationId, setSelectedQuotationId] = useState([]);
  const [searchResultData, setSearchResultData] = useState([]);
  const [searchDataLoading, setSearchDataLoading] = useState(false);

  useEffect(() => {
    if (isEmpty(emailCustomerList)) return;
    let tempSorted = emailCustomerList?.filter(
      (customer) => !isEmpty(customer?.item)
    );
    if (isEmpty(tempSorted)) return;

    let customerIds = [];
    // get all customer ids from the tempSorted array and set like this [ "1" , "2" , "3" ] etc
    // skip falsy ids so we never search with Customer_Id: [undefined]
    tempSorted.forEach((customer) => {
      if (customer?.item?.Customer_Id) {
        customerIds.push(customer.item.Customer_Id);
      }
    });
    // check if there is duplicate customer ids and remove them
    let uniqueCustomerIds = [...new Set(customerIds)];

    // get all the jobs for the customer ids
    setSortedCustomerId(uniqueCustomerIds);
  }, [emailCustomerList]);

  // Change #100688 - when nobody on the To line resolves to a customer, start
  // from the record's own customer (the invoice's, say) so every tab and the
  // Job List load without a manual pick. Once only, so clearing the box sticks.
  const fallbackPrefilled = useRef(false);
  useEffect(() => {
    if (fallbackPrefilled.current || !fallbackCustomerId) return;
    if (!isEmpty(sortedCustomerId)) return;
    fallbackPrefilled.current = true;
    setSortedCustomerId([fallbackCustomerId]);
  }, [fallbackCustomerId, sortedCustomerId]);

  const jobsPrefilled = useRef(false);
  useEffect(() => {
    if (jobsPrefilled.current || isEmpty(fallbackJobIds)) return;
    if (!isEmpty(selectedJobsId)) return;
    jobsPrefilled.current = true;
    setSelectedJobsId(fallbackJobIds);
  }, [fallbackJobIds, selectedJobsId]);

  const { customersListAPI, customersDataListLoading } = useSelector(
    (state) => ({
      customersListAPI: state.Customers?.customersDataList?.customer_list,
      customersDataListLoading: state.Customers?.customersDataListLoading,
    })
  );

  useEffect(() => {
    // No customer resolved yet -> don't fire a search with an empty/invalid
    // Customer_Id, otherwise its empty result overwrites a good one (the shared
    // Redux slot is also read by the Job List dropdown) and the list looks empty.
    if (isEmpty(sortedCustomerId)) return;
    if (
      activeSearchTab === "jobAttachments" ||
      activeSearchTab === "checklist"
    ) {
      dispatch(
        getSingleCustomerJobsList([
          {
            job_data: {
              Customer_Id: sortedCustomerId,
            },
          },
        ])
      );
    }
    if (activeSearchTab === "invoices") {
      dispatch(
        searchSingleCustomerInvoiceList([
          {
            invoice_data: {
              Customer_Id: sortedCustomerId,
            },
          },
        ])
      );
    }
    if (activeSearchTab === "quotations") {
      dispatch(
        getSingleCustomerQuoteList([
          {
            customer_data: {
              Id: sortedCustomerId,
            },
          },
        ])
      );
    }
    // Change #100688 - the checklist search lives in the effect below only. It
    // used to fire here too (customer only) alongside the job-filtered one, and
    // whichever answered last won, so a picked job often showed every job.
    if (activeSearchTab === "proformaInvoices") {
      dispatch(
        proformaInvoiceSearch([
          {
            customer_data: { Id: sortedCustomerId },
          },
        ])
      );
    }
  }, [sortedCustomerId, activeSearchTab]);

  useEffect(() => {
    if (isEmpty(sortedCustomerId)) {
      setSearchResultData([]);
      return;
    }
    console.log("activeSearchTab", activeSearchTab);
    if (
      activeSearchTab === "jobAttachments" ||
      activeSearchTab === "checklist"
    ) {
      if (isEmpty(selectedJobsId)) {
        dispatch(
          searchSingleJobFilesList([
            {
              customer_data: {
                Id: sortedCustomerId,
              },
            },
          ])
        );
      }
      activeSearchTab === "checklist" &&
        dispatch(
          taskCheckListSearch([
            {
              customer_data: {
                Id: sortedCustomerId,
              },
              ...(selectedJobsId
                ? {
                    jobattachment_data: {
                      Job_Id: selectedJobsId,
                    },
                  }
                : {}),
            },
          ])
        );
      if (activeSearchTab === "jobAttachments") {
        dispatch(
          searchSingleJobFilesList([
            {
              jobattachment_data: {
                Job_Id: selectedJobsId,
              },
              customer_data: {
                Id: sortedCustomerId,
              },
            },
          ])
        );
      }
    }
  }, [activeSearchTab, selectedJobsId, sortedCustomerId]);

  const { singleJobFilesListAPI } = useSelector((state) => ({
    singleJobFilesListAPI: state.Jobs.singleJobFilesList?.jobattachment_list,
  }));
  const { singleCustomerInvoiceListAPI } = useSelector((state) => ({
    singleCustomerInvoiceListAPI:
      state.InvoiceCrmIT.singleCustomerInvoiceList?.invoice_list,
  }));
  console.log("singleCustomerInvoiceListAPI", singleCustomerInvoiceListAPI);

  const { taskChecklistSearchAPI } = useSelector((state) => ({
    taskChecklistSearchAPI:
      state.Checklist.taskChecklistSearch?.taskchecklist_list,
  }));
  const { proformaInvoiceSearchDataAPI } = useSelector((state) => ({
    proformaInvoiceSearchDataAPI:
      state.ProformaInvoice.invoiceSearchData?.proformainvoice_list,
  }));
  const { SingleCustomerQuoteListAPI } = useSelector((state) => ({
    SingleCustomerQuoteListAPI: state.Quote.SingleCustomerQuoteList?.quote_list,
  }));
  console.log("SingleCustomerQuoteListAPI", SingleCustomerQuoteListAPI);

  useEffect(() => {
    if (activeSearchTab === "jobAttachments") {
      if (isEmpty(singleJobFilesListAPI)) {
        setSearchResultData([]);
      } else {
        setSearchResultData(singleJobFilesListAPI);
      }
    }
    if (activeSearchTab === "invoices") {
      if (isEmpty(singleCustomerInvoiceListAPI)) {
        setSearchResultData([]);
      } else {
        setSearchResultData(singleCustomerInvoiceListAPI);
      }
    }
    if (activeSearchTab === "checklist") {
      if (isEmpty(taskChecklistSearchAPI)) {
        setSearchResultData([]);
      } else {
        setSearchResultData(taskChecklistSearchAPI);
      }
    }
    if (activeSearchTab === "proformaInvoices") {
      if (isEmpty(proformaInvoiceSearchDataAPI)) {
        setSearchResultData([]);
      } else {
        setSearchResultData(proformaInvoiceSearchDataAPI);
      }
    }
    if (activeSearchTab === "quotations") {
      if (isEmpty(SingleCustomerQuoteListAPI)) {
        setSearchResultData([]);
      } else {
        setSearchResultData(SingleCustomerQuoteListAPI);
      }
    }
  }, [
    activeSearchTab,
    singleJobFilesListAPI,
    singleCustomerInvoiceListAPI,
    taskChecklistSearchAPI,
    proformaInvoiceSearchDataAPI,
    SingleCustomerQuoteListAPI,
  ]);
  //dispaly attachment results
  const [activeColumn, setActiveColumn] = useState([]);

  console.log("tempEmailAttachments", attachments);
  const JobAttachmentColumns = useMemo(
    () => [
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <span className={"like-href"}> #{jobs.row.original.Id}</span>
            </div>
          </>
        ),
      },
      {
        // job id  with beginning #Job_Id
        Header: "Job Id",
        accessor: "Job_Id",
        filterable: false,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <span className={"like-href"}> #{jobs.row.original.Job_Id}</span>
            </div>
          </>
        ),
      },
      {
        Header: "File Name",
        accessor: "FileAttachment_Name",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.FileAttachment_Name}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) => (
          <>
            <div className="d-flex align-items-center">
              {/*   Attach to Email*/}
              {attachments.find((item) => item.id === cell.row.original.Id) ? (
                <button
                  className={"btn btn-danger"}
                  onClick={() => {
                    setAttachments(
                      attachments.filter(
                        (item) => item.id !== cell.row.original.Id
                      )
                    );
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  className={"btn btn-primary"}
                  onClick={() => {
                    // find lid if exists find max number + 1 if dose not exist set to 1
                    let lid = 1;
                    if (attachments.length > 0) {
                      lid =
                        Math.max.apply(
                          Math,
                          attachments.map(function (o) {
                            return o.lid;
                          })
                        ) + 1;
                    }
                    setAttachments([
                      ...attachments,
                      {
                        id: cell.row.original.Id,
                        type: "JobAttachment",
                        label: cell.row.original?.FileAttachment_Name,
                        lid: lid,
                      },
                    ]);
                  }}
                >
                  Attach To Email
                </button>
              )}
            </div>
          </>
        ),
      },
    ],
    [searchResultData, attachments]
  );

  //Checkllist Columns
  const ChecklistColumns = useMemo(
    () => [
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <span className={"like-href"}> #{jobs.row.original.Id}</span>
            </div>
          </>
        ),
      },
      {
        Header: "Task_Id",
        accessor: "Task_Id",
      },
      {
        Header: "Checklist Name",
        accessor: "Title",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Title}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Job_Id",
        accessor: "Job_Id",
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) => (
          <>
            <div className="d-flex align-items-center">
              {/*   Attach to Email*/}
              {attachments.find((item) => item.id === cell.row.original.Id) ? (
                <button
                  className={"btn btn-danger"}
                  onClick={() => {
                    setAttachments(
                      attachments.filter(
                        (item) => item.id !== cell.row.original.Id
                      )
                    );
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  className={"btn btn-primary"}
                  onClick={() => {
                    // find lid if exists find max number + 1 if dose not exist set to 1
                    let lid = 1;
                    if (attachments.length > 0) {
                      lid =
                        Math.max.apply(
                          Math,
                          attachments.map(function (o) {
                            return o.lid;
                          })
                        ) + 1;
                    }
                    setAttachments([
                      ...attachments,
                      {
                        id: cell.row.original.Id,
                        type: "Checklist",
                        label: cell.row.original?.Title,
                        lid: lid,
                      },
                    ]);
                  }}
                >
                  Attach To Email
                </button>
              )}
            </div>
          </>
        ),
      },
    ],
    [searchResultData, attachments]
  );

  const invoicesColumns = useMemo(
    () => [
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <span className={"like-href"}> #{jobs.row.original.Id}</span>
            </div>
          </>
        ),
      },
      {
        Header: "Invoice Title",
        accessor: "Title",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Title}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Customer",
        accessor: "Customer_FullName",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Customer_FullName}
              </div>
            </div>
          </>
        ),
      },

      {
        Header: "Invoice Date",
        accessor: "InvoiceDateTime",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {HandleDateTime(jobs.row.original?.InvoiceDateTime)}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Total",
        accessor: "AmountLocalTotal",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                £{jobs.row.original?.AmountLocalTotal}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) => (
          <>
            <div className="d-flex align-items-center">
              {/*   Attach to Email*/}
              {attachments.find((item) => item.id === cell.row.original.Id) ? (
                <button
                  className={"btn btn-danger"}
                  onClick={() => {
                    setAttachments(
                      attachments.filter(
                        (item) => item.id !== cell.row.original.Id
                      )
                    );
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  className={"btn btn-primary"}
                  onClick={() => {
                    // find lid if exists find max number + 1 if dose not exist set to 1
                    let lid = 1;
                    if (attachments.length > 0) {
                      lid =
                        Math.max.apply(
                          Math,
                          attachments.map(function (o) {
                            return o.lid;
                          })
                        ) + 1;
                    }
                    setAttachments([
                      ...attachments,
                      {
                        id: cell.row.original.Id,
                        type: "Invoice",
                        label:
                          "#" +
                          cell.row.original.Id +
                          " " +
                          cell.row.original?.Title,
                        lid: lid,
                      },
                    ]);
                  }}
                >
                  Attach To Email
                </button>
              )}
            </div>
          </>
        ),
      },
    ],
    [searchResultData, attachments]
  );
  const proformaInvoicesColumns = useMemo(
    () => [
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <span className={"like-href"}> #{jobs.row.original.Id}</span>
            </div>
          </>
        ),
      },
      {
        Header: "Proforma Invoice Title",
        accessor: "Title",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Title}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Customer",
        accessor: "Customer_FullName",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Customer_FullName}
              </div>
            </div>
          </>
        ),
      },

      {
        Header: "Proforma Invoice Date",
        accessor: "InvoiceDateTime",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {HandleDateTime(jobs.row.original?.ProformaInvoiceDateTime)}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Total",
        accessor: "AmountLocalTotal",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                £{jobs.row.original?.AmountLocalTotal}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) => (
          <>
            <div className="d-flex align-items-center">
              {/*   Attach to Email*/}
              {attachments.find((item) => item.id === cell.row.original.Id) ? (
                <button
                  className={"btn btn-danger"}
                  onClick={() => {
                    setAttachments(
                      attachments.filter(
                        (item) => item.id !== cell.row.original.Id
                      )
                    );
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  className={"btn btn-primary"}
                  onClick={() => {
                    // find lid if exists find max number + 1 if dose not exist set to 1
                    let lid = 1;
                    if (attachments.length > 0) {
                      lid =
                        Math.max.apply(
                          Math,
                          attachments.map(function (o) {
                            return o.lid;
                          })
                        ) + 1;
                    }
                    setAttachments([
                      ...attachments,
                      {
                        id: cell.row.original.Id,
                        type: "ProformaInvoice",
                        label:
                          "#" +
                          cell.row.original.Id +
                          " " +
                          cell.row.original?.Title,
                        lid: lid,
                      },
                    ]);
                  }}
                >
                  Attach To Email
                </button>
              )}
            </div>
          </>
        ),
      },
    ],
    [searchResultData, attachments]
  );
  const quoteColumns = useMemo(
    () => [
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <span className={"like-href"}> #{jobs.row.original.Id}</span>
            </div>
          </>
        ),
      },
      {
        Header: "Title",
        accessor: "Title",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Title}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Customer",
        accessor: "Customer_FullName",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {jobs.row.original?.Customer_FullName}
              </div>
            </div>
          </>
        ),
      },

      {
        Header: "Quote Date",
        accessor: "CreatedDateTime",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {HandleDateTime(jobs.row.original?.CreatedDateTime)}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Total",
        accessor: "AmountLocalTotal",
        filterable: true,
        Cell: (jobs) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                £{jobs.row.original?.AmountLocalTotal}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) => (
          <>
            <div className="d-flex align-items-center">
              {/*   Attach to Email*/}
              {/* Type-checked too: ids are only unique within a type. */}
              {attachments.find(
                (item) =>
                  item.type === "Quote" && item.id === cell.row.original.Id
              ) ? (
                <button
                  className={"btn btn-danger"}
                  onClick={() => {
                    setAttachments(
                      attachments.filter(
                        (item) =>
                          !(
                            item.type === "Quote" &&
                            item.id === cell.row.original.Id
                          )
                      )
                    );
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  className={"btn btn-primary"}
                  onClick={() => {
                    // find lid if exists find max number + 1 if dose not exist set to 1
                    let lid = 1;
                    if (attachments.length > 0) {
                      lid =
                        Math.max.apply(
                          Math,
                          attachments.map(function (o) {
                            return o.lid;
                          })
                        ) + 1;
                    }
                    setAttachments([
                      ...attachments,
                      {
                        id: cell.row.original.Id,
                        // Change #100125 - was "Quotations", which SendEmail
                        // never sends (it filters on "Quote"), so a picked
                        // quotation was silently left off the email.
                        type: "Quote",
                        label:
                          "#" +
                          cell.row.original.Id +
                          " " +
                          cell.row.original?.Title,
                        lid: lid,
                      },
                    ]);
                  }}
                >
                  Attach To Email
                </button>
              )}
            </div>
          </>
        ),
      },
    ],
    [searchResultData, attachments]
  );
  // Proforma Columns

  // Change #100123 - files kept on the customer record. Read straight from
  // /customerattachment/search rather than through redux: nothing else on the
  // page shares them. Declared after the effects above so that, when the To
  // line has no customer but the record does, this answer is the one kept.
  const customerIdsForFiles = useMemo(() => {
    if (!isEmpty(sortedCustomerId)) return sortedCustomerId;
    return fallbackCustomerId ? [fallbackCustomerId] : [];
  }, [sortedCustomerId, fallbackCustomerId]);

  useEffect(() => {
    if (activeSearchTab !== "customerAttachments") return undefined;
    if (isEmpty(customerIdsForFiles)) {
      setSearchResultData([]);
      return undefined;
    }
    let cancelled = false;
    setSearchDataLoading(true);
    searchCustomerAttachmentApiCall([
      { customerattachment_data: { Customer_Id: customerIdsForFiles } },
    ])
      .then((response) => {
        if (!cancelled)
          setSearchResultData(response?.customerattachment_list || []);
      })
      .catch(() => {
        if (!cancelled) setSearchResultData([]);
      })
      .finally(() => {
        if (!cancelled) setSearchDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeSearchTab, customerIdsForFiles]);

  // Ids are only unique within a type, so "already attached" checks the type
  // too - a customer file #5 is not job attachment #5.
  const isAttached = (type, id) =>
    attachments.some((item) => item.type === type && item.id === id);

  const CustomerAttachmentColumns = useMemo(
    () => [
      {
        Header: "File Name",
        accessor: "FileAttachment_Name",
        filterable: true,
        Cell: (cell) => (
          <div className="d-flex align-items-center">
            <i className="ri-file-line me-2"></i>
            {cell.row.original?.FileAttachment_Name ||
              `File #${cell.row.original?.FileAttachment_Id}`}
          </div>
        ),
      },
      {
        Header: "Added",
        accessor: "CreatedDateTime",
        filterable: false,
        Cell: (cell) => (
          <span>{HandleDateTime(cell.row.original?.CreatedDateTime)}</span>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) =>
          isAttached("CustomerAttachment", cell.row.original.Id) ? (
            <button
              className={"btn btn-danger"}
              onClick={() =>
                setAttachments(
                  attachments.filter(
                    (item) =>
                      !(
                        item.type === "CustomerAttachment" &&
                        item.id === cell.row.original.Id
                      )
                  )
                )
              }
            >
              Remove
            </button>
          ) : (
            <button
              className={"btn btn-primary"}
              onClick={() =>
                setAttachments([
                  ...attachments,
                  {
                    id: cell.row.original.Id,
                    type: "CustomerAttachment",
                    label: cell.row.original?.FileAttachment_Name,
                  },
                ])
              }
            >
              Attach To Email
            </button>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResultData, attachments]
  );

  useEffect(() => {
    switch (activeSearchTab) {
      case "customerAttachments":
        setActiveColumn(CustomerAttachmentColumns);
        break;
      case "invoices":
        setActiveColumn(invoicesColumns);
        break;
      case "quotations":
        setActiveColumn(quoteColumns);
        break;
      case "jobAttachments":
        setActiveColumn(JobAttachmentColumns);
        break;
      case "proformaInvoices":
        setActiveColumn(proformaInvoicesColumns);
        break;
      case "checklist":
        setActiveColumn(ChecklistColumns);
        break;
      default:
        setActiveColumn([]);
        break;
    }
  }, [activeSearchTab, attachments]);
  return (
    <div>
      <React.Fragment>
        <div className={""}>
          <div className={""}>
            <div>
              <Row className="row align-items-end mt-3">
                <Col>
                  <div id="mail-filter-navlist">
                    <Nav
                      className="nav nav-tabs nav-tabs-custom nav-primary gap-1 text-center border-bottom-0"
                      role="tablist"
                    >
                      <NavItem>
                        <NavLink
                          className={classnames(
                            { active: activeSearchTab === "invoices" },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            setActiveSearchTab("invoices");
                          }}
                          href="#"
                        >
                          <i className="fa-regular fa-folder-open"></i>
                          <span className="ms-1 d-none d-sm-inline-block">
                            Invoices
                          </span>
                        </NavLink>
                      </NavItem>{" "}
                      <NavItem>
                        <NavLink
                          className={classnames(
                            {
                              active: activeSearchTab === "proformaInvoices",
                            },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            setActiveSearchTab("proformaInvoices");
                          }}
                          href="#"
                        >
                          <i className="fa-regular fa-folder-open"></i>
                          <span className="ms-1 d-none d-sm-inline-block">
                            Proforma Invoices
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames(
                            { active: activeSearchTab === "quotations" },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            setActiveSearchTab("quotations");
                          }}
                          href="#"
                        >
                          <i className="fa-regular fa-folder-open"></i>
                          <span className="ms-1 d-none d-sm-inline-block">
                            Quotations
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames(
                            { active: activeSearchTab === "checklist" },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            setActiveSearchTab("checklist");
                          }}
                          href="#"
                        >
                          <i className="fa-regular fa-folder-open"></i>
                          <span className="ms-1 d-none d-sm-inline-block">
                            Task Checklist
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames(
                            {
                              active: activeSearchTab === "jobAttachments",
                            },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            setActiveSearchTab("jobAttachments");
                          }}
                          href="#"
                        >
                          <i className="fa-regular fa-folder-open"></i>
                          <span className="ms-1 d-none d-sm-inline-block">
                            Job Attachments
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames(
                            {
                              active: activeSearchTab === "customerAttachments",
                            },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            setActiveSearchTab("customerAttachments");
                          }}
                          href="#"
                        >
                          <i className="fa-regular fa-folder-open"></i>
                          <span className="ms-1 d-none d-sm-inline-block">
                            Customer Attachments
                          </span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Col>
              </Row>
              {activeSearchTab && (
                <div className="pt-0">
                  <div>
                    <div className={"mt-2"}>
                      <Label>Customers List</Label>
                      <Select
                        className=" "
                        value={
                          isEmpty(sortedCustomerId)
                            ? null
                            : sortedCustomerId.map((customerId) => ({
                                // String compare: the record's customer id can
                                // arrive as a number, the list's as a string.
                                label: customersListAPI?.find(
                                  (item) =>
                                    String(item.Id) === String(customerId)
                                )?.FullName,
                                value: customerId,
                                id: customerId,
                              }))
                        }
                        placeholder={"Select Customer"}
                        isMulti={true}
                        options={
                          !isEmpty(customersListAPI)
                            ? customersListAPI?.map((item) => ({
                                label: item.FullName,
                                value: item.Id,
                                id: item.Id,
                              }))
                            : []
                        }
                        onChange={(e) => {
                          // update sorted customer id add remove from list
                          setSortedCustomerId(e.map((item) => item.value));
                        }}
                        id="country-select"
                      ></Select>
                    </div>
                    {(activeSearchTab === "jobAttachments" ||
                      activeSearchTab === "checklist") && (
                      <div className={"mt-2"}>
                        <Label>Job List</Label>
                        <Select
                          className=" "
                          value={
                            isEmpty(selectedJobsId)
                              ? null
                              : selectedJobsId.map((jobId) => ({
                                  label:
                                    "#" +
                                    jobId +
                                    " " +
                                    (singleCustomerJobsListDataAPI?.find(
                                      (item) =>
                                        String(item.Id) === String(jobId)
                                    )?.Title ?? ""),
                                  value: jobId,
                                  id: jobId,
                                }))
                          }
                          placeholder={"Select Job"}
                          isMulti={true}
                          options={
                            !isEmpty(singleCustomerJobsListDataAPI)
                              ? singleCustomerJobsListDataAPI?.map((item) => ({
                                  label: "#" + item.Id + " " + item.Title,
                                  value: item.Id,
                                  id: item.Id,
                                }))
                              : []
                          }
                          onChange={(e) => {
                            // update sorted customer id add remove from list
                            setSelectedJobsId(e.map((item) => item.value));
                          }}
                          id="country-select"
                        ></Select>
                      </div>
                    )}

                    {searchDataLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        {searchResultData?.length > 0 ? (
                          <TableContainer
                            columns={activeColumn}
                            data={searchResultData}
                            isGlobalFilter={false}
                            isAddUserList={false}
                            customPageSize={10}
                            className="custom-header-css"
                            divClass="table-responsive table-card mb-0"
                            tableClass="align-middle table-wrapper-scroll-y my-custom-scrollbar"
                            theadClass="text-muted"
                            isLeadsFilter={false}
                          />
                        ) : (
                          <div>
                            <div className="text-center mt-2">
                              <h2>No Records Found</h2>
                              <p className="text-muted">
                                Try to adjust your search to find what you are
                                looking for.
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    </div>
  );
};

export default SearchAttachments;
